import { useEffect } from "react";
import { useForm, type Control } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DIALOG_FORM_CLASSES,
  DialogBody,
} from "@/components/ui/dialog";
import { useCloseGuard } from "@/hooks/use-close-guard";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import {
  domainReconcileSchema,
  type DomainReconcileFormData,
} from "@/schemas/adminDomainReconcile.schema";
import type { DomainOperationDto } from "@/types/domain";

interface DomainReconcileDialogProps {
  /** The domain being reconciled; null closes the dialog. */
  domain: DomainOperationDto | null;

  /** Called when the dialog is dismissed. */
  onClose: () => void;

  /** Called with the validated form data when the admin submits. */
  onSubmit: (data: DomainReconcileFormData) => void;

  /** Whether the reconcile mutation is in flight. */
  isSubmitting?: boolean;
}

/**
 * Manual-reconcile dialog for a domain registered out-of-band.
 *
 * The admin enters the registrar's reference and the confirmed expiry
 * date; on submit the parent runs the reconcile mutation.
 */
export function DomainReconcileDialog({
  domain,
  onClose,
  onSubmit,
  isSubmitting,
}: DomainReconcileDialogProps) {
  const form = useForm<DomainReconcileFormData>({
    resolver: zodResolver(domainReconcileSchema),
    defaultValues: {
      registrarReference: domain?.registrarReference ?? "",
      expiresAt: "",
    },
  });

  const typedControl =
    form.control as unknown as Control<DomainReconcileFormData>;

  // Reset the form whenever a different domain is selected.
  useEffect(() => {
    if (domain) {
      form.reset({
        registrarReference: domain.registrarReference ?? "",
        expiresAt: "",
      });
    }
  }, [domain, form]);

  const open = domain !== null;

  const handleClose = () => {
    form.reset({ registrarReference: "", expiresAt: "" });
    onClose();
  };

  const guard = useCloseGuard({
    open,
    onOpenChange: (open) => !open && handleClose(),
    isDirty: form.formState.isDirty,
    isSubmitting,
    onDiscard: () => form.reset(),
  });

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Reconcile {domain?.fqdn}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className={DIALOG_FORM_CLASSES}
            >
              <DialogBody className="space-y-4">
                <DialogDescription>
                  If this domain was registered directly at the registrar, enter
                  the registrar reference and confirmed expiry date to mark the
                  registration Active.
                </DialogDescription>
                <FormField
                  control={typedControl}
                  name="registrarReference"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Registrar Reference</FormLabel>
                      <FormControl>
                        <Input placeholder="RC-123456" {...field} />
                      </FormControl>
                      <FormDescription>
                        The registrar&apos;s domain or order ID.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={typedControl}
                  name="expiresAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Expiry Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormDescription>
                        The confirmed expiry date at the registrar.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </DialogBody>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => guard.onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Reconcile
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      {guard.dialog}
    </>
  );
}
